import { ChangeDetectionStrategy, Component, computed, inject, Injector, input } from '@angular/core';
import { NgxFileDropEntry } from 'node_modules/ngx-file-drop/ngx-file-drop/ngx-file-drop-entry';
import { AuthService } from 'src/app/services/auth.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { VideoToThumbnailService } from 'src/app/services/video-to-thumbnail.service';
import { CreateEventFormStore } from '../../new-event-form/_services/create-event-form-store.service';
import { EditEventFormStore } from '../../edit-event-form/_services/edit-event-form-store.service';

@Component({
  selector: 'media-upload-step',
  standalone: false,
  templateUrl: './media-upload-step.component.html',
  styleUrl: './media-upload-step.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MediaUploadStepComponent {
  edit = input<boolean>(false);

  allowedVideoExtensions: string[] = [
    '.mp4',
    '.mov'
  ];
  allowedImageExtensions: string[] = [
    '.jpg',
    '.jpeg',
    '.png'
  ];

  private injector = inject(Injector);

  public eventFormStore = computed(() =>
    this.edit()
      ? this.injector.get(EditEventFormStore)
      : this.injector.get(CreateEventFormStore)
  );

  public authService = inject(AuthService);

  private readonly materialSnackbar = inject(MatSnackBar);

  private readonly videoToThumbnailService = inject(VideoToThumbnailService);

  public get streappEvent() {
    return this.eventFormStore().streappEvent;
  }

  public async dropped(files: NgxFileDropEntry[], uploadSite: string) {
    for (const droppedFile of files) {

      // Is it a file?
      if (droppedFile.fileEntry.isFile) {
        const extension = droppedFile.fileEntry.name.substr(droppedFile.fileEntry.name.lastIndexOf('.')).toLowerCase();
        // Is the file allowed?
        if (!this.allowedVideoExtensions.includes(extension) && !this.allowedImageExtensions.includes(extension)) {
          this.materialSnackbar.open(
            `Bestand type is niet toegestaan. Toegestaande bestandtypes: ${this.allowedImageExtensions.concat(this.allowedVideoExtensions)}`,
            'Ok',
            {
              duration: 10000,
              panelClass: 'snackbar-fail',
            }
          );

          console.warn(`File type ${extension} is not allowed.`);
          return;
        }

        // Check to make sure this file hasn't already been added in both instances and all mediaitems list
        if (
          this.streappEvent()!.preUploadedMediaItems
            .map((item) => item.file.relativePath)
            .indexOf(droppedFile.relativePath) === -1 &&
          this.streappEvent()!.preUploadedShopMediaItems
            .map((item) => item.file.relativePath)
            .indexOf(droppedFile.relativePath) === -1 &&
          uploadSite == 'none' &&
          this.streappEvent()!.preUploadedMediaItems.length < 10
        ) {
          await this.saveFileToMediaItems(droppedFile, uploadSite);
        }

        // Check to make sure this file hasn't already been added to both instances and shop-only list
        if (
          this.streappEvent()!.preUploadedShopMediaItems
            .map((item) => item.file.relativePath)
            .indexOf(droppedFile.relativePath) === -1 &&
          this.streappEvent()!.preUploadedMediaItems
            .map((item) => item.file.relativePath)
            .indexOf(droppedFile.relativePath) === -1 &&
          uploadSite == 'shop' &&
          this.streappEvent()!.preUploadedShopMediaItems.length < 10
        ) {
          await this.saveFileToMediaItems(droppedFile, uploadSite);
        }
      }
    }

    if (this.eventFormStore().streappEvent().preUploadedMediaItems.length > 0) {
      this.eventFormStore().stepMediaItemsUploadValid.set(true);
      this.eventFormStore().stepPrivacyValid.set(false);
      this.eventFormStore().stepPriceValid.set(false);
    } else {
      // Edit doesn't require any mediaItems to be uploaded
      this.eventFormStore().stepMediaItemsUploadValid.set(this.edit() ? true : false);
      this.eventFormStore().stepPrivacyValid.set(true);
      this.eventFormStore().stepPriceValid.set(true);
    }
  }

  async saveFileToMediaItems(
    droppedFile: NgxFileDropEntry,
    saveToList: string
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
      const reader = new FileReader();

      fileEntry.file((file) => {
        reader.readAsDataURL(file);
        reader.onload = async () => {
          if (file.type.startsWith('video') || this.isVideoFile(file.name)) {
            // Create a thumbnail if its a video
            const url = await this.videoToThumbnailService.generateThumbnail(
              file
            );
            // Push video with previewimage and prices to media items
            this.eventFormStore().addMediaItems({
              file: droppedFile,
              previewFile: url,
              price: '20.00',
              suggestedPrice: '20.00',
              priceChanged: false,
              finished: false,
            }, saveToList);
            resolve();
          } else if (file.type.startsWith('image')) {
            // Push image with and prices to media items
            this.eventFormStore().addMediaItems({
              file: droppedFile,
              previewFile: reader.result,
              price: '15.00',
              suggestedPrice: '15.00',
              priceChanged: false,
              finished: false,
            }, saveToList);
            resolve();
          } else {
            reject('File was not returned as image or video');
          }
        };
      });
    });
  }

  public removeFile(i: number, listName: string): void {
    this.eventFormStore().removeMediaItem(i, listName);
    if (this.eventFormStore().streappEvent().preUploadedMediaItems.length === 0) {
      // Edit doesn't require any mediaItems to be uploaded
      this.eventFormStore().stepMediaItemsUploadValid.set(this.edit() ? true : false);
      this.eventFormStore().stepPrivacyValid.set(false);
      this.eventFormStore().stepPriceValid.set(this.edit() ? true : false);
    }
  }

  private isVideoFile(fileName: string): boolean {
    const videoExtensions = [
      '.mp4',
      '.avi',
      '.wmv',
      '.mov',
      '.flv',
      '.mkv',
      '.m4v',
      '.mpg',
      '.mpeg',
      '.webm',
      '.3gp',
      '.ogv',
      '.m2v',
      '.m2ts',
      '.mts',
      '.ts',
      '.vob',
      '.divx',
      '.xvid',
      '.rm',
      '.rmvb',
      '.asf',
      '.f4v',
      '.mpe',
      '.mpv',
      '.mpeg4',
      '.qt',
      '.dav',
      '.swf',
    ];

    const extension = fileName.substr(fileName.lastIndexOf('.')).toLowerCase();
    return videoExtensions.includes(extension);
  }
}
